import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, AlertTriangle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import type { AssistantCommandView } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getFriendlyErrorMessage, isApiClientError } from "@/lib/api-client";
import { useConfirmCommand, useRejectCommand } from "../../hooks";
import { SecretReveal } from "./SecretReveal";
import { ACTION_LABEL } from "./action-labels";

// Card de ação: proposto (confirmar/dispensar + countdown do TTL), executado (link do
// resultRef), falhou (mensagem amigável), expirado (re-pedir). Ações `safe` (scenario.preview, sem
// expiresAt) auto-confirmam ao renderizar — sem card, resultado direto.

function resultLink(command: AssistantCommandView): { to: string; label: string } | null {
  const ref = command.resultRef ?? {};
  if (typeof ref.interpretationId === "string")
    return { to: `/interpretations/${ref.interpretationId}`, label: "Ver interpretação" };
  if (typeof ref.simulationId === "string")
    return { to: `/simulations/${ref.simulationId}`, label: "Ver simulação" };
  if (typeof ref.batchId === "string") return { to: `/simulations`, label: "Ver sugestões" };
  return null;
}

function useSecondsLeft(expiresAt: string | null | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-background p-3 text-sm">{children}</div>
  );
}

export function ActionCard({
  command,
  conversationId,
}: {
  command: AssistantCommandView;
  conversationId: string | null;
}) {
  const navigate = useNavigate();
  const confirm = useConfirmCommand(conversationId);
  const reject = useRejectCommand(conversationId);
  const isSafe = command.riskTier === "SAFE";
  const isDestructive = command.riskTier === "DESTRUCTIVE";
  const secondsLeft = useSecondsLeft(command.expiresAt);
  const fired = useRef(false);
  const [typed, setTyped] = useState("");

  // Ações safe auto-confirmam ao aparecer (sem card) — mesma rota/RBAC/idempotência, só muda a UX.
  useEffect(() => {
    if (!fired.current && isSafe && command.status === "PROPOSED") {
      fired.current = true;
      confirm.mutate({ command });
    }
  }, [isSafe, command, confirm]);

  // Destrutivo (finish/cancel): SEMPRE exige digitar o nome exato da entidade (paridade ConfirmDialog).
  // O card ao vivo (payload do SSE) ainda não tem `entityName` — mantém o Confirmar travado até o
  // detalhe reidratar com o nome (nunca habilita sem o gate, o service rejeitaria com 409).
  const nameOk =
    !isDestructive || (command.entityName != null && typed.trim() === command.entityName);
  const needsName = isDestructive && command.entityName != null; // input visível + envia o nome

  const label = ACTION_LABEL[command.name] ?? command.name;
  const confirmErrorCode = isApiClientError(confirm.error) ? confirm.error.error.code : null;
  const expiredByError = confirmErrorCode === "ASSISTANT_COMMAND_EXPIRED";

  if (command.status === "EXECUTED") {
    const link = resultLink(command);
    // O segredo (rawApiKey) só existe na resposta viva do confirm — nunca no command persistido.
    // Após invalidar, o command reidrata como EXECUTED mas confirm.data preserva o segredo (1x).
    const secret = confirm.data?.oneTimeSecret;
    return (
      <Shell>
        <div className="flex items-center gap-2 text-foreground">
          <CheckCircle2 className="h-4 w-4 text-[var(--rd-success-500)]" aria-hidden="true" />
          <span>{label} — concluído.</span>
        </div>
        {secret ? <SecretReveal secret={secret} /> : null}
        {link ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-2 gap-1.5"
            onClick={() => navigate(link.to)}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {link.label}
          </Button>
        ) : null}
      </Shell>
    );
  }

  if (command.status === "FAILED") {
    return (
      <Shell>
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{command.error ?? "Não foi possível executar a ação."}</span>
        </div>
      </Shell>
    );
  }

  if (command.status === "EXPIRED" || expiredByError || (secondsLeft === 0 && !isSafe)) {
    return (
      <Shell>
        <p className="text-muted-foreground">Esta proposta expirou — peça de novo ao assistente.</p>
      </Shell>
    );
  }

  // Safe em curso (auto-confirm) — sem botões.
  if (isSafe) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden="true" />
          <span>{label}…</span>
        </div>
        {confirm.isError && !expiredByError ? (
          <p className="mt-1 text-xs text-destructive">{getFriendlyErrorMessage(confirm.error)}</p>
        ) : null}
      </Shell>
    );
  }

  // PROPOSED tier confirm/destructive — card com botões + countdown.
  return (
    <Shell>
      <div className="flex items-center gap-2 font-medium text-foreground">
        {isDestructive ? (
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        ) : null}
        <span>{label}</span>
      </div>
      {secondsLeft != null ? (
        <div className="mt-0.5 text-xs text-muted-foreground">Expira em {fmt(secondsLeft)}</div>
      ) : null}
      {needsName ? (
        <div className="mt-2">
          <label htmlFor={`confirm-${command.id}`} className="text-xs text-muted-foreground">
            Ação irreversível. Digite{" "}
            <span className="font-medium text-foreground">{command.entityName}</span> para
            confirmar.
          </label>
          <Input
            id={`confirm-${command.id}`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="mt-1 h-8"
          />
        </div>
      ) : isDestructive ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Ação irreversível — carregando confirmação…
        </p>
      ) : null}
      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          variant={isDestructive ? "destructive" : "default"}
          onClick={() =>
            confirm.mutate({ command, confirmationName: needsName ? typed.trim() : undefined })
          }
          disabled={confirm.isPending || !nameOk}
        >
          {confirm.isPending ? "Confirmando…" : "Confirmar"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => reject.mutate(command.id)}
          disabled={reject.isPending}
        >
          Dispensar
        </Button>
      </div>
      {confirm.isError && !expiredByError ? (
        <p className="mt-1 text-xs text-destructive">{getFriendlyErrorMessage(confirm.error)}</p>
      ) : null}
    </Shell>
  );
}
